import type {
  CreateDictionaryPermissionDto,
  UpdateDirectoryPermissionDto,
} from '@data-delivery/permission-ui-api';
import { directoryPermissionApi } from '@data-delivery/permission-ui-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { permissionQueryClient as qc } from '../query-client';

export const directoryPermissionKeys = {
  all: ['directory-permissions'] as const,
  list: () => [...directoryPermissionKeys.all, 'list'] as const,
  detail: (id: string) => [...directoryPermissionKeys.all, 'detail', id] as const,
};

type DirectoryPermissionEntity = NonNullable<
  Awaited<ReturnType<typeof directoryPermissionApi.getAllDirectoryPermissions>>['data']
>[number];

export function useDirectoryPermissionsQuery() {
  return useQuery<DirectoryPermissionEntity[]>({
    queryKey: directoryPermissionKeys.list(),
    async queryFn() {
      const { data } = await directoryPermissionApi.getAllDirectoryPermissions();
      return data ?? [];
    },
  });
}

export function useDirectoryPermissionQuery(id: string | undefined) {
  return useQuery<DirectoryPermissionEntity | undefined>({
    queryKey: id ? directoryPermissionKeys.detail(id) : ['directory-permissions', 'detail', 'none'],
    enabled: Boolean(id),
    async queryFn() {
      if (!id) {
        return undefined;
      }

      const { data } = await directoryPermissionApi.getDirectoryPermissionById({ id });
      return data ?? undefined;
    },
  });
}

export function useCreateDirectoryPermissionMutation() {
  return useMutation<
    DirectoryPermissionEntity | undefined,
    unknown,
    CreateDictionaryPermissionDto,
    { previousList: DirectoryPermissionEntity[]; optimisticId: string }
  >({
    mutationKey: [...directoryPermissionKeys.all, 'create'],
    async mutationFn(dto) {
      const { data } = await directoryPermissionApi.createDirectoryPermission({
        createDictionaryPermissionDto: dto,
      });
      return data ?? undefined;
    },
    async onMutate(dto) {
      await qc.cancelQueries({ queryKey: directoryPermissionKeys.list() });
      const previousList =
        qc.getQueryData<DirectoryPermissionEntity[]>(directoryPermissionKeys.list()) || [];
      const optimistic: DirectoryPermissionEntity = {
        id: `temp-${Date.now()}`,
        name: dto.name,
        permission: dto.permission,
        relativePaths: dto.relativePaths ?? [],
        lastModified: Date.now(),
        lastModifiedBy: 'optimistic',
      };
      qc.setQueryData(directoryPermissionKeys.list(), [...previousList, optimistic]);
      return { previousList, optimisticId: optimistic.id };
    },
    onError(_error, _dto, context) {
      if (context?.previousList) {
        qc.setQueryData(directoryPermissionKeys.list(), context.previousList);
      }
    },
    onSuccess(serverEntity, _dto, context) {
      if (!serverEntity || !context?.optimisticId) {
        return;
      }

      const list =
        qc.getQueryData<DirectoryPermissionEntity[]>(directoryPermissionKeys.list()) || [];
      qc.setQueryData(
        directoryPermissionKeys.list(),
        list.map(p => (p.id === context.optimisticId ? serverEntity : p)),
      );
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: directoryPermissionKeys.list() });
    },
  });
}

export function useUpdateDirectoryPermissionMutation(id: string) {
  type UpdateInput = {
    lastModified: number;
    name?: string;
    permission?: string[];
    relativePaths?: string[];
  };
  return useMutation<
    DirectoryPermissionEntity | undefined,
    unknown,
    UpdateInput,
    { previousList: DirectoryPermissionEntity[]; previousDetail?: DirectoryPermissionEntity }
  >({
    mutationKey: [...directoryPermissionKeys.all, 'update', id],
    async mutationFn(dto) {
      const payload: UpdateDirectoryPermissionDto = {
        lastModified: dto.lastModified,
      };
      if (dto.name !== undefined) {
        payload.name = dto.name;
      }

      if (dto.permission !== undefined) {
        payload.permission = dto.permission;
      }

      if (dto.relativePaths !== undefined) {
        payload.relativePaths = dto.relativePaths;
      }

      const { data } = await directoryPermissionApi.updateDirectoryPermission({
        id,
        updateDirectoryPermissionDto: payload,
      });
      return data ?? undefined;
    },
    async onMutate(dto) {
      await qc.cancelQueries({ queryKey: directoryPermissionKeys.list() });
      await qc.cancelQueries({ queryKey: directoryPermissionKeys.detail(id) });
      const previousList =
        qc.getQueryData<DirectoryPermissionEntity[]>(directoryPermissionKeys.list()) || [];
      const previousDetail = qc.getQueryData<DirectoryPermissionEntity>(
        directoryPermissionKeys.detail(id),
      );
      const nextList = previousList.map(p => {
        if (p.id !== id) {
          return p;
        }

        return {
          ...p,
          name: dto.name === undefined ? p.name : dto.name,
          permission: dto.permission === undefined ? p.permission : dto.permission,
          relativePaths: dto.relativePaths === undefined ? p.relativePaths : dto.relativePaths,
          lastModified: dto.lastModified,
        };
      });
      qc.setQueryData(directoryPermissionKeys.list(), nextList);
      if (previousDetail) {
        qc.setQueryData(directoryPermissionKeys.detail(id), {
          ...previousDetail,
          name: dto.name === undefined ? previousDetail.name : dto.name,
          permission: dto.permission === undefined ? previousDetail.permission : dto.permission,
          relativePaths:
            dto.relativePaths === undefined ? previousDetail.relativePaths : dto.relativePaths,
          lastModified: dto.lastModified,
        });
      }

      return { previousList, previousDetail };
    },
    onError(_error, _dto, context) {
      if (context?.previousList) {
        qc.setQueryData(directoryPermissionKeys.list(), context.previousList);
      }

      if (context?.previousDetail) {
        qc.setQueryData(directoryPermissionKeys.detail(id), context.previousDetail);
      }
    },
    onSuccess(serverEntity) {
      if (!serverEntity) {
        return;
      }

      const list =
        qc.getQueryData<DirectoryPermissionEntity[]>(directoryPermissionKeys.list()) || [];
      qc.setQueryData(
        directoryPermissionKeys.list(),
        list.map(p => (p.id === id ? { ...p, ...serverEntity } : p)),
      );
      const detail = qc.getQueryData<DirectoryPermissionEntity>(directoryPermissionKeys.detail(id));
      if (detail) {
        qc.setQueryData(directoryPermissionKeys.detail(id), { ...detail, ...serverEntity });
      }
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: directoryPermissionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: directoryPermissionKeys.list() });
    },
  });
}

export function useDeleteDirectoryPermissionMutation() {
  return useMutation<unknown, unknown, string, { previousList: DirectoryPermissionEntity[] }>({
    mutationKey: [...directoryPermissionKeys.all, 'delete'],
    async mutationFn(id: string) {
      const { data } = await directoryPermissionApi.deleteDirectoryPermission({ id });
      return data;
    },
    async onMutate(id: string) {
      await qc.cancelQueries({ queryKey: directoryPermissionKeys.list() });
      const previousList =
        qc.getQueryData<DirectoryPermissionEntity[]>(directoryPermissionKeys.list()) || [];
      qc.setQueryData(
        directoryPermissionKeys.list(),
        previousList.filter(p => p.id !== id),
      );
      return { previousList };
    },
    onError(_error, _id, context) {
      if (context?.previousList) {
        qc.setQueryData(directoryPermissionKeys.list(), context.previousList);
      }
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: directoryPermissionKeys.list() });
    },
  });
}
